import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  type PaintRule,
  PolygonSymbolizer,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

interface Props {
  defaultVisible: boolean;
  visible?: boolean;
}

// UIに表示するレイヤー名
export const HIGH_LEVEL_USE_AREA_LAYER_NAME = "高度利用地区";

// pmtilesが内部で保持しているレイヤー名
const HIGH_LEVEL_USE_AREA_DATA_LAYER_NAME = "koudori";

const host = process.env.VITE_COMMON_TILE_HOST ?? "";
const url = `${host}/koudori-tiles/koudori_2024_z18.pmtiles`;

// ズームレベル
export const HIGH_LEVEL_USE_AREA_MIN_ZOOM = 10;
export const HIGH_LEVEL_USE_AREA_MAX_ZOOM = 22;
const MIN_ZOOM = HIGH_LEVEL_USE_AREA_MIN_ZOOM;
const MAX_ZOOM = HIGH_LEVEL_USE_AREA_MAX_ZOOM;

// ポリゴンのスタイルを設定
const FILL_COLOR = "#FBE5B8";
const FILL_OPACITY = 0.7;
const STROKE_COLOR = "#FBE5B8";
const STROKE_WIDTH = 4;

// 右下に表示する項目
const attribution =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

export const buildHighLevelUseAreaPaintRules = (
  renderScale: number,
  zoomOffset: number
): PaintRule[] => [
  {
    dataLayer: HIGH_LEVEL_USE_AREA_DATA_LAYER_NAME,
    symbolizer: new PolygonSymbolizer({
      fill: FILL_COLOR,
      opacity: FILL_OPACITY,
      stroke: STROKE_COLOR,
      width: STROKE_WIDTH * renderScale,
    }),
    minzoom: MIN_ZOOM + zoomOffset,
    maxzoom: MAX_ZOOM + zoomOffset,
  },
];

/**
 * 高度利用地区レイヤー
 */
export const HighLevelUseAreaLayer: React.FC<Props> = ({
  defaultVisible,
  visible: visibleProp,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      leafletLayer({
        url,
        paintRules: buildHighLevelUseAreaPaintRules(
          context.printScale,
          context.zoomOffset
        ),
        attribution,
      }) as GridLayer,
    []
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === HIGH_LEVEL_USE_AREA_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === HIGH_LEVEL_USE_AREA_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!map) return;

    const layer = leafletLayer({
      url,
      paintRules: buildHighLevelUseAreaPaintRules(1, 0),
      attribution,
    }) as GridLayer;

    layer.setZIndex(LAYER_Z_INDEX.HIGH_LEVEL_USE_AREA);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible]);

  return <FeatureGroup />;
};
